import { FunctionComponent, useContext, useState } from 'react'
import { ThemeContext } from 'styled-components'

import DollarSign from 'design-system/Iconography/SVG/DollarSign'

import { useElement, useWindowSize } from 'services/hooks'

import PriceRangeDropdown from '../Dropdowns/PriceRangeDropdown'
import PriceMobileSheet from '../MobileSheets/PriceMobileSheet'
import { IFilterProps, IMobileSheetProps, IPriceRangeProps } from '../types'
import FilterButton from './Shared/FilterButton'

const formatPrice = (price: number) => {
  if (price < 1000000) return `$${(price / 1000).toFixed(0)}k`

  return `$${(price / 1000000).toFixed(2).replace(/\.?0+$/, '')}m`
}

const renderIcon = () => <DollarSign />

const PriceFilter: FunctionComponent<IFilterProps & IPriceRangeProps & IMobileSheetProps> = ({
  selectedPriceRange,
  setSelectedPriceRange,
  applyPriceRangeSelection,
  resetPriceRangeSelection,
  ...mobileFilterSheetProps
}) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isMobile = width && width < theme.metrics.tablet

  const [applied, setApplied] = useState(false)
  const [label, setLabel] = useState('Price')
  const [visible, setVisible] = useState(false)
  const [error, setError] = useState('')

  const [buttonRef, buttonElement] = useElement<HTMLDivElement>()

  const clearSelection = () => {
    resetPriceRangeSelection()
    setLabel('Price')
    setApplied(false)
    setVisible(false)
  }

  const handleApply = () => {
    if (!selectedPriceRange.min && !selectedPriceRange.max) return clearSelection()

    setApplied(true)
    applyPriceRangeSelection()

    const middleTerm = selectedPriceRange.min && selectedPriceRange.max ? '-' : ''
    const formattedMin = selectedPriceRange.min ? formatPrice(selectedPriceRange.min) : 'Up to '
    const formattedMax = selectedPriceRange.max ? formatPrice(selectedPriceRange.max) : '+'
    setLabel(`${formattedMin}${middleTerm}${formattedMax}`)

    setVisible(false)
  }

  // must be initialized or UI will show wrong UI to start
  if (!width) return null

  const refProps = isMobile ? {} : { ref: buttonRef }
  const onClickProps = isMobile ? { onClick: mobileFilterSheetProps.showMobileFilterSheet } : {}

  return (
    <>
      <FilterButton
        active={applied || visible}
        label={label}
        renderIcon={renderIcon}
        onClearSelection={clearSelection}
        clearable={applied}
        {...refProps}
        {...onClickProps}
      />
      <PriceMobileSheet
        selectedPriceRange={selectedPriceRange}
        setSelectedPriceRange={setSelectedPriceRange}
        onApply={handleApply}
        onReset={clearSelection}
        {...mobileFilterSheetProps}
      />
      <PriceRangeDropdown
        buttonElement={buttonElement}
        visible={visible}
        setVisible={setVisible}
        error={error}
        setError={setError}
        onApply={handleApply}
        selectedPriceRange={selectedPriceRange}
        setSelectedPriceRange={setSelectedPriceRange}
        applyPriceRangeSelection={applyPriceRangeSelection}
        resetPriceRangeSelection={clearSelection}
      />
    </>
  )
}

export default PriceFilter
